import axios from 'axios';

export const BACKEND_RETRY_MAX_RETRIES = 20;
export const BACKEND_RETRY_BASE_DELAY_MS = 250;
export const BACKEND_RETRY_MAX_DELAY_MS = 4000;
const DEFAULT_JITTER_MAX_MS = 250;
const USER_DATA_RESIDENCY_STORAGE_KEY = 'peekapak.userDataResidency';

export type BackendRetryDataResidency = 'USA' | 'ARE';

type SleepFunction = (delayMs: number) => Promise<void>;
type JitterFunction = () => number;

export interface BackendRetryOptions {
  dataResidency?: BackendRetryDataResidency;
  sleep?: SleepFunction;
  getJitterMs?: JitterFunction;
}

export class BackendHttpError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export function getBackendRetryDelayMs(retryIndex: number, jitterMs = 0): number {
  const exponentialDelay = BACKEND_RETRY_BASE_DELAY_MS * 2 ** retryIndex;
  return Math.min(exponentialDelay + jitterMs, BACKEND_RETRY_MAX_DELAY_MS);
}

export function getBackendErrorHttpStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('status' in error && typeof error.status === 'number') {
    return error.status;
  }

  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error.statusCode;
  }

  if ('response' in error && error.response && typeof error.response === 'object') {
    const { response } = error;
    if ('status' in response && typeof response.status === 'number') {
      return response.status;
    }
  }

  if ('request' in error && error.request && typeof error.request === 'object') {
    const { request } = error;
    if ('status' in request && typeof request.status === 'number') {
      return request.status;
    }
  }

  return undefined;
}

export function getDefaultBackendJitterMs(): number {
  return Math.floor(Math.random() * DEFAULT_JITTER_MAX_MS);
}

export function sleepForBackendRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

function getCurrentRetryDataResidency(): BackendRetryDataResidency {
  const storedDataResidency = localStorage.getItem(USER_DATA_RESIDENCY_STORAGE_KEY);
  // Treat the legacy stored 'uae' value as ARE
  return storedDataResidency === 'ARE' || storedDataResidency === 'uae' ? 'ARE' : 'USA';
}

function isLikelyRetryableNetworkError(error: unknown): boolean {
  const message = (() => {
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.code === 'ERR_NETWORK') {
        return 'network error';
      }

      return error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return error.message;
    }

    return '';
  })().toLowerCase();

  return (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('network error') ||
    message.includes('err_timed_out') ||
    message.includes('failed to fetch')
  );
}

export function shouldRetryBackendErrorForResidency(dataResidency: BackendRetryDataResidency, error: unknown): boolean {
  if (dataResidency !== 'ARE') {
    return false;
  }

  const statusCode = getBackendErrorHttpStatusCode(error);
  if (statusCode !== undefined) {
    return statusCode >= 500;
  }

  return isLikelyRetryableNetworkError(error);
}

export async function retryBackendRequestForResidency<T>(
  operationName: string,
  operation: () => Promise<T>,
  options: BackendRetryOptions = {},
): Promise<T> {
  const dataResidency = options.dataResidency ?? getCurrentRetryDataResidency();
  const sleepFunction = options.sleep ?? sleepForBackendRetry;
  const getJitterMs = options.getJitterMs ?? getDefaultBackendJitterMs;

  let retryCount = 0;

  while (true) {
    try {
      return await operation();
    } catch (error) {
      if (!shouldRetryBackendErrorForResidency(dataResidency, error) || retryCount >= BACKEND_RETRY_MAX_RETRIES) {
        throw error;
      }

      const delayMs = getBackendRetryDelayMs(retryCount, getJitterMs());
      const statusCode = getBackendErrorHttpStatusCode(error);

      retryCount += 1;
      console.warn(
        `[BackendRetry] ${operationName} failed with status ${statusCode ?? 'unknown'} ` +
          `for the fra backend, retry ${retryCount}/${BACKEND_RETRY_MAX_RETRIES} after ${delayMs}ms`,
      );

      await sleepFunction(delayMs);
    }
  }
}
